// src/pages/member/MemberResourcesModelDocuments.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";

const pageBg = "#f9fbfe";
const sectionHeaderBg = "#c4d8ec";
const sectionHeaderBorder = "#4e81c4";
const linkBlue = "#005695";

const getFileIcon = (type) => {
  const t = (type || "").toLowerCase();
  if (t === "pdf") return <PictureAsPdfIcon sx={{ fontSize: 18, color: "#fff" }} />;
  if (t === "xlsx") return <TableChartIcon sx={{ fontSize: 18, color: "#fff" }} />;
  return <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />; // doc / docx / default
};

/* ---------- Data ---------- */

const generalUse = {
  title: "General Use Model Documents",
  groups: [
    {
      heading: "Model Contract Documents",
      description:
        "Approved by Primary Authority – Primary Authority–approved documents have been checked for legal compliance, giving you confidence they meet consumer protection standards.",
      items: [
        {
          title: "Terms and Conditions (docx)",
          description:
            "Sets out the work, payment, timelines, warranties, and responsibilities for supplying goods and services.",
          type: "docx",
          href: "/assets/model-docs/general/terms-and-conditions.docx",
        },
        {
          title: "Cancellation form (docx)",
          description: "Explains how a customer can end the contract.",
          type: "docx",
          href: "/assets/model-docs/general/cancellation-form.docx",
        },
        {
          title: "Express request form (docx)",
          description:
            "Confirms a customer’s agreement to begin work before the usual cancellation period ends.",
          type: "docx",
          href: "/assets/model-docs/general/express-request-form.docx",
        },
      ],
    },
    {
      heading: "Other Model Documents",
      items: [
        {
          title: "Complaints procedure (docx)",
          description:
            "Explains how a customer can raise an issue and how they can expect you to respond and help resolve the problem.",
          type: "docx",
          href: "/assets/model-docs/general/complaints-procedure.docx",
        },
        {
          title: "Complaints log (xlsx)",
          description:
            "Records customer complaints and tracks how they are managed and resolved.",
          type: "xlsx",
          href: "/assets/model-docs/general/complaints-log.xlsx",
        },
        {
          title: "Installer warranty (doc)",
          description:
            "Guarantees the quality of the installation work and covers fixing faults within a set period.",
          type: "doc",
          href: "/assets/model-docs/general/installer-warranty.doc",
        },
        {
          title: "Use of Personal Data Notice (pdf)",
          description:
            "Explains how consumer data may be collected and used, which must be shared with customers at first contact.",
          type: "pdf",
          href: "/assets/model-docs/general/use-of-personal-data-notice.pdf",
        },
      ],
    },
  ],
};

const solarPvAndBattery = {
  title: "Solar PV and Battery Model Documents",
  groups: [
    {
      heading: "Model documents",
      items: [
        {
          title: "Model proposal letter (docx)",
          type: "docx",
          href: "/assets/model-docs/solar-pv/model-proposal-letter.docx",
        },
        {
          title: "Model performance estimate (doc)",
          type: "doc",
          href: "/assets/model-docs/solar-pv/model-performance-estimate.doc",
        },
        {
          title: "Model quote and order form (docx)",
          type: "docx",
          href: "/assets/model-docs/solar-pv/model-quote-and-order-form.docx",
        },
      ],
    },
    {
      heading: "Guidance",
      description:
        "Guidance on how to offer clear and fair proposals and how to present performance information and performance predictions for solar PV contracts.",
      items: [
        {
          title: "Solar PV Performance Presentation (pdf)",
          type: "pdf",
          href:
            "/assets/model-docs/solar-pv/solar-pv-performance-presentation.pdf",
        },
      ],
    },
  ],
};

const solarThermal = {
  title: "Solar Thermal Model Documents",
  groups: [
    {
      heading: "Model documents",
      items: [
        {
          title: "Solar Thermal Model Quote 2022 (docx)",
          type: "docx",
          href: "/assets/model-docs/solar-thermal/solar-thermal-model-quote-2022.docx",
        },
        {
          title: "Solar Thermal Model Performance Estimate 2022 (docx)",
          type: "docx",
          href:
            "/assets/model-docs/solar-thermal/solar-thermal-model-performance-estimate-2022.docx",
        },
      ],
    },
    {
      heading: "Guidance",
      description:
        "Guidance on how to offer clear and fair proposals and how to present performance information and performance predictions for solar thermal contracts.",
      items: [
        {
          title: "Consumer Proposals Solar Thermal (pdf)",
          type: "pdf",
          href:
            "/assets/model-docs/solar-thermal/consumer-proposals-solar-thermal.pdf",
        },
        {
          title: "Performance Information Solar Thermal (pdf)",
          type: "pdf",
          href:
            "/assets/model-docs/solar-thermal/performance-information-solar-thermal.pdf",
        },
      ],
    },
  ],
};

const heatPumps = {
  title: "Heat Pumps Model Documents",
  groups: [
    {
      heading: "Model documents",
      items: [
        {
          title: "Heat Pump Model Quote 2022 (docx)",
          type: "docx",
          href: "/assets/model-docs/heat-pumps/heat-pump-model-quote-2022.docx",
        },
        {
          title: "Heat Pump Model Performance Estimate 2022 (docx)",
          type: "docx",
          href:
            "/assets/model-docs/heat-pumps/heat-pump-model-performance-estimate-2022.docx",
        },
      ],
    },
    {
      heading: "Guidance",
      description:
        "Guidance on how to offer clear and fair proposals and how to present performance information and performance predictions for heat pump contracts.",
      items: [
        {
          title: "Consumer Proposals Heat Pumps (pdf)",
          type: "pdf",
          href:
            "/assets/model-docs/heat-pumps/consumer-proposals-heat-pumps.pdf",
        },
        {
          title: "Performance Information Heat Pumps (pdf)",
          type: "pdf",
          href:
            "/assets/model-docs/heat-pumps/performance-information-heat-pumps.pdf",
        },
      ],
    },
  ],
};

const biomass = {
  title: "Biomass Model Documents",
  groups: [
    {
      heading: "Model documents",
      items: [
        {
          title: "Biomass Model Quote 2022 (docx)",
          type: "docx",
          href: "/assets/model-docs/biomass/biomass-model-quote-2022.docx",
        },
        {
          title: "Biomass Model Performance Estimate 2022 (docx)",
          type: "docx",
          href:
            "/assets/model-docs/biomass/biomass-model-performance-estimate-2022.docx",
        },
      ],
    },
    {
      heading: "Guidance",
      description:
        "Guidance on how to offer clear and fair proposals and how to present performance information and performance predictions for biomass contracts.",
      items: [
        {
          title: "Guidance On Consumer Proposals Biomass (pdf)",
          type: "pdf",
          href:
            "/assets/model-docs/biomass/guidance-consumer-proposals-biomass.pdf",
        },
        {
          title: "Guidance On Performance Information Biomass (pdf)",
          type: "pdf",
          href:
            "/assets/model-docs/biomass/guidance-performance-information-biomass.pdf",
        },
      ],
    },
  ],
};

const sections = [generalUse, solarPvAndBattery, solarThermal, heatPumps, biomass];

/* ---------- UI components ---------- */

function SectionCard({ section }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #dde4f0",
        mb: 4,
      }}
    >
      <Box
        sx={{
          backgroundColor: sectionHeaderBg,
          borderTop: `4px solid ${sectionHeaderBorder}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1d2736" }}>
          {section.title}
        </Typography>
      </Box>

      <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
        {section.groups.map((group, groupIndex) => (
          <Box key={group.heading} sx={{ mb: groupIndex < section.groups.length - 1 ? 3 : 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: group.description ? 0.5 : 1, color: "#111827" }}
            >
              {group.heading}
            </Typography>

            {group.description && (
              <Typography
                variant="body2"
                sx={{ mb: 1.5, color: "#4b5563", maxWidth: 900 }}
              >
                {group.description}
              </Typography>
            )}

            <List disablePadding>
              {group.items.map((item, itemIndex) => (
                <React.Fragment key={item.title}>
                  <ListItemButton
                    component="a"
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      alignItems: "flex-start",
                      px: 0,
                      "&:hover .linkText": { textDecoration: "underline" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 48, mt: 0.5 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: sectionHeaderBorder,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {getFileIcon(item.type)}
                      </Box>
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography
                          className="linkText"
                          sx={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: linkBlue,
                          }}
                        >
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        item.description && (
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.5, color: "#4a4f59", lineHeight: 1.5 }}
                          >
                            {item.description}
                          </Typography>
                        )
                      }
                    />
                  </ListItemButton>

                  {itemIndex < group.items.length - 1 && (
                    <Divider sx={{ my: 1.75, borderColor: "#edf1f7" }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

export default function MemberResourcesModelDocuments() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
          Home &gt; Member Resources &gt; Model Documents
        </Typography>

        {/* Page title + intro */}
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "#111827" }}>
          Model Documents
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: "#4b5563" }}>
          A library of model contracts, notices, and guidance to support compliant and
          transparent consumer agreements.
        </Typography>

        {sections.map((section) => (
          <SectionCard key={section.title} section={section} />
        ))}
      </Container>
    </Box>
  );
}
